import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "lucide-react";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [nameForm, setNameForm] = useState({ name: user?.name || "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [nameSaving, setNameSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [nameError, setNameError] = useState("");
  const [pwError, setPwError] = useState("");

  const saveName = async (e) => {
    e.preventDefault();
    if (!nameForm.name.trim()) { setNameError("Name cannot be empty."); return; }
    setNameError(""); setNameSaving(true);
    try {
      const res = await client.put(`/companies/users/${user._id}`, { name: nameForm.name.trim() });
      const meRes = await client.get("/auth/me");
      setUser(meRes.data);
      setNameSuccess("Display name updated!");
      setTimeout(() => setNameSuccess(""), 3000);
    } catch (err) { setNameError(formatApiError(err)); }
    finally { setNameSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { setPwError("Passwords do not match."); return; }
    if (pwForm.new_password.length < 6) { setPwError("Password must be at least 6 characters."); return; }
    setPwError(""); setPwSaving(true);
    try {
      await client.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwSuccess("Password changed successfully!");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setTimeout(() => setPwSuccess(""), 3000);
    } catch (err) { setPwError(formatApiError(err)); }
    finally { setPwSaving(false); }
  };

  const roleBadge = { super_admin: "bg-purple-100 text-purple-700", company_admin: "bg-[#e6f2f5] text-[#006080]", company_user: "bg-gray-100 text-gray-600" };
  const roleLabel = { super_admin: "Super Admin", company_admin: "Company Admin", company_user: "Company User" };

  return (
    <div className="p-6 max-w-2xl mx-auto fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">My Profile</h1>
        <p className="text-gray-500 text-sm mt-0.5">Manage your account information and password</p>
      </div>

      {/* Account info */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-[#006080] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <p className="font-bold text-gray-900 text-lg">{user?.name}</p>
            <p className="text-gray-500 text-sm">{user?.email}</p>
            <span className={`text-xs px-2 py-0.5 rounded font-medium mt-1 inline-block ${roleBadge[user?.role] || "bg-gray-100"}`}>
              {roleLabel[user?.role] || user?.role}
            </span>
          </div>
        </div>

        {/* Display name form */}
        <form onSubmit={saveName} className="space-y-3 border-t border-gray-100 pt-4">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Display Name</p>
          {nameError && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{nameError}</div>}
          {nameSuccess && <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm flex items-center gap-2"><CheckCircle size={14} />{nameSuccess}</div>}
          <div className="flex gap-3">
            <Input
              className="flex-1"
              value={nameForm.name}
              onChange={e => setNameForm({ name: e.target.value })}
              placeholder="Your full name"
            />
            <Button type="submit" className="bg-[#006080] hover:bg-[#004d66] text-white" disabled={nameSaving}>
              {nameSaving ? "Saving..." : "Update Name"}
            </Button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-4">Change Password</p>
        <form onSubmit={savePassword} className="space-y-4">
          {pwError && <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{pwError}</div>}
          {pwSuccess && <div className="p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm flex items-center gap-2"><CheckCircle size={14} />{pwSuccess}</div>}
          <div>
            <Label className="text-xs text-gray-500">Current Password</Label>
            <Input className="mt-1" type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} placeholder="Enter current password" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">New Password</Label>
              <Input className="mt-1" type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Min. 6 characters" required />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Confirm New Password</Label>
              <Input className="mt-1" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Repeat new password" required />
            </div>
          </div>
          <Button type="submit" className="bg-[#006080] hover:bg-[#004d66] text-white" disabled={pwSaving}>
            {pwSaving ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
